import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clinician } from '../../types';
import { getStateDetails } from "../../utils/getStateDetails"; // Make sure the path is correct
import { useInviteSimplePracticeClinician } from '../../hooks/Clinic/useInviteSimplePracticeClinician ';

import { 
  Eye, 
  UserCheck, 
  Calendar, 
  DollarSign, 
  Shield, 
  FileText, 
  Loader2,
  User,
  GraduationCap,
  Clock,
  Users,
  MapPin,
  Mail,
  Phone,
  Award,
  Languages,
  Activity,
  CalendarDays,
  CheckCircle,
  XCircle,
  Clock3
} from 'lucide-react';

interface ClinicianAdminDetailsProps {
  clinician: Clinician;
  onEdit?: () => void;
  isLoading?: boolean;
}

const ClinicianAdminDetails: React.FC<ClinicianAdminDetailsProps> = ({ 
  clinician, 
  onEdit, 
  isLoading = false 
}) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const { inviteOne, loading: inviteLoading } = useInviteSimplePracticeClinician();

  const handleSendInvite = async () => {
    if (!clinician.email) {
      alert('No email address found for this clinician');
      return;
    }
    
    
    const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];
    const practice_name = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:practice_name"];

    
    const payload = {
      clinicianId: clinician._id || clinician.id,
      email: clinician.email,
      first_name: clinician.firstName,
      last_name: clinician.lastName,
      practice_id: practiceId,
      practice_name: practice_name
    }

    const ok = await inviteOne(payload);
    if (ok) {
      alert('Invite sent successfully');
    }
  };

  // Memoized calculations based on new data structure
  const metrics = useMemo(() => {
    const openSlots = clinician?.availability?.open?.length || 0;
    const bookedSlots = clinician?.availability?.booked?.length || 0;
    const cancelledSlots = clinician?.availability?.cancelled?.length || 0;
    const outOfOfficeSlots = clinician?.availability?.outOfOffice?.length || 0;
    const blockedSlots = clinician?.availability?.blocked?.length || 0;
    const specialties = clinician?.specialties || [];
    
    return {
      openSlots,
      bookedSlots,
      cancelledSlots,
      outOfOfficeSlots,
      blockedSlots,
      specialtyCount: specialties.length,
      totalSlots: openSlots + bookedSlots + cancelledSlots + outOfOfficeSlots + blockedSlots
    };
  }, [clinician]);

  // Optimized animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        delayChildren: 0.1,
        staggerChildren: 0.05 
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  const cardHoverVariants = {
    hover: { 
      y: -4,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: { duration: 0.2 }
    }
  };

  // Loading state
  if (isLoading || !clinician) {
    return (
      <div className="w-full p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600 font-medium">Loading clinician details...</p>
        </motion.div>
      </div>
    );
  }

  // Format display name using new structure
  const displayName = clinician.fullName || `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim();
  const status = clinician.status || 'pending';

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="w-full p-4 md:p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen"
    >
      {/* Enhanced Header */}
      <motion.div 
        variants={itemVariants}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <div className="text-sm text-gray-500 mb-2">Dashboard / Clinicians / Details / {displayName}</div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
              <Eye className="mr-3 text-blue-600" size={28} />
              {displayName}
              <span className="ml-2 text-lg text-gray-500 font-normal">({clinician.title || 'Dr.'})</span>
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge status={status} />
              
            </div>
          </div>
            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center shadow-lg font-medium"
              onClick={handleSendInvite}
              >
                <Mail className="mr-2" size={20} />
                Send Invite
              </motion.button>
        </div>
      </motion.div>

      {/* Enhanced Metrics Dashboard */}
      <motion.div 
        variants={itemVariants}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8"
      >
        <MetricCard
          icon={<Calendar className="text-emerald-600" size={24} />}
          title="Open Slots"
          value={metrics.openSlots}
          bgColor="bg-gradient-to-br from-emerald-50 to-emerald-100"
          textColor="text-emerald-800"
        />
        <MetricCard
          icon={<CheckCircle className="text-blue-600" size={24} />}
          title="Booked Appointments"
          value={metrics.bookedSlots}
          bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
          textColor="text-blue-800"
        />
        <MetricCard
          icon={<XCircle className="text-red-600" size={24} />}
          title="Cancelled"
          value={metrics.cancelledSlots}
          bgColor="bg-gradient-to-br from-red-50 to-red-100"
          textColor="text-red-800"
        />
        <MetricCard
          icon={<Activity className="text-orange-600" size={24} />}
          title="Specialties"
          value={metrics.specialtyCount}
          bgColor="bg-gradient-to-br from-orange-50 to-orange-100"
          textColor="text-orange-800"
        />
      </motion.div>

      {/* Enhanced Profile Information */}
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-8">
            <ProfileSection 
              icon={<User className="text-blue-600" size={24} />}
              title="Personal Information"
            >
              <div className="space-y-4">
                <InfoRow label="Full Name" value={displayName} />
                <InfoRow label="Professional Title" value={clinician.title || 'Not specified'} />
                <InfoRow label="Clinician ID" value={clinician.clinicianId} />
                <InfoRow label="NPI Number" value={clinician.npiNumber || 'Not specified'} />
                {clinician.email && <InfoRow label="Email" value={clinician.email} />}
                <InfoRow label="Slug" value={clinician.slug} />
                <InfoRow label="Practice ID" value={clinician.id_practice} />
              </div>
            </ProfileSection>

            <ProfileSection 
              icon={<Activity className="text-purple-600" size={24} />}
              title="Specialties"
            >
              <div className="grid grid-cols-1 gap-2">
                {clinician.specialties?.map((specialty, index) => (
                  <motion.div 
                    key={index}
                    variants={cardHoverVariants}
                    whileHover="hover"
                    className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200"
                  >
                    <p className="font-medium text-purple-800">{specialty}</p>
                  </motion.div>
                )) || <p className="text-gray-500 italic">No specialties listed</p>}
              </div>
            </ProfileSection>

            <ProfileSection 
              icon={<Award className="text-yellow-600" size={24} />}
              title="Licenses"
            >
              <div className="space-y-3">
                {clinician.licenses?.length > 0 ? (
                  clinician.licenses.map((license, index) => {
                    const stateInfo = getStateDetails(license.state);

                    return (
                      <motion.div 
                        key={index}
                        variants={cardHoverVariants}
                        whileHover="hover"
                        className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200"
                      >
                        <p className="font-semibold text-gray-900">License # {index + 1}</p>
                        <p className="text-sm text-gray-600">Number: {license.licenseNumber}</p>
                        <p className="text-sm text-gray-600">Type: {license.type}</p>
                        <p className="text-sm text-gray-600">
                          State: {stateInfo.stateName} ({stateInfo.stateCode})
                        </p>
                        <p className="text-sm text-gray-600">
                          Country: {stateInfo.countryName}
                        </p>
                        <p className="text-sm text-gray-600">Expiration: {license.expirationDate}</p>
                      </motion.div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 italic">No license information available</p>
                )}
              </div>
            </ProfileSection>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            <ProfileSection 
              icon={<CalendarDays className="text-green-600" size={24} />}
              title="Availability Summary"
            >
              <div className="space-y-3">
                <AvailabilityMetricCard
                  title="Open Slots"
                  count={metrics.openSlots}
                  color="emerald"
                  icon={<Calendar className="text-emerald-600" size={20} />}
                />
                <AvailabilityMetricCard
                  title="Booked Appointments"
                  count={metrics.bookedSlots}
                  color="blue"
                  icon={<CheckCircle className="text-blue-600" size={20} />}
                />
                <AvailabilityMetricCard
                  title="Cancelled"
                  count={metrics.cancelledSlots}
                  color="red"
                  icon={<XCircle className="text-red-600" size={20} />}
                />
                <AvailabilityMetricCard
                  title="Out of Office"
                  count={metrics.outOfOfficeSlots}
                  color="orange"
                  icon={<Clock3 className="text-orange-600" size={20} />}
                />
                <AvailabilityMetricCard
                  title="Blocked"
                  count={metrics.blockedSlots}
                  color="gray"
                  icon={<Shield className="text-gray-600" size={20} />}
                />
              </div>
            </ProfileSection>

            <ProfileSection 
              icon={<Clock className="text-indigo-600" size={24} />}
              title="Timestamps"
            >
              <div className="space-y-3">
                <InfoRow 
                  label="Availability Updated" 
                  value={new Date(clinician.availabilityUpdatedAt).toLocaleString()} 
                />
                <InfoRow 
                  label="Last Calendar Scrape" 
                  value={new Date(clinician.lastCalendarScrape).toLocaleString()} 
                />
                <InfoRow 
                  label="Created" 
                  value={new Date(clinician.createdAt).toLocaleString()} 
                />
                <InfoRow 
                  label="Updated" 
                  value={new Date(clinician.updatedAt).toLocaleString()} 
                />
              </div>
            </ProfileSection>
          </div>
        </div>

        {/* Availability Details */}
        {/* <motion.div 
          variants={itemVariants}
          className="mt-8 pt-8 border-t border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <CalendarDays className="mr-2 text-indigo-600" size={24} />
            Availability Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AvailabilitySection 
              title="Open Slots" 
              events={clinician.availability?.open || []} 
              color="emerald"
            />
            <AvailabilitySection 
              title="Booked Appointments" 
              events={clinician.availability?.booked || []} 
              color="blue"
            />
            <AvailabilitySection 
              title="Cancelled" 
              events={clinician.availability?.cancelled || []} 
              color="red"
            />
            <AvailabilitySection 
              title="Out of Office" 
              events={clinician.availability?.outOfOffice || []} 
              color="orange"
            />
            <AvailabilitySection 
              title="Blocked" 
              events={clinician.availability?.blocked || []} 
              color="gray"
            />
          </div>
        </motion.div> */}
      </motion.div>
    </motion.div>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig = {
    verified: { color: 'bg-green-100 text-green-700', label: 'Verified' },
    pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
    active: { color: 'bg-blue-100 text-blue-700', label: 'Active' },
    inactive: { color: 'bg-gray-100 text-gray-700', label: 'Inactive' }
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  );
};

// Optimized Utility Components
const MetricCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string | number;
  bgColor: string;
  textColor: string;
}> = ({ icon, title, value, bgColor, textColor }) => (
  <motion.div 
    variants={{
      hover: { 
        y: -2,
        transition: { duration: 0.2 }
      }
    }}
    whileHover="hover"
    className={`${bgColor} p-4 md:p-6 rounded-xl shadow-md border border-white/50`}
  >
    <div className="flex items-center justify-between mb-3">
      {icon}
    </div>
    <div className={`text-xl md:text-2xl font-bold ${textColor} mb-1`}>
      {value}
    </div>
    <div className="text-xs md:text-sm text-gray-600 font-medium">
      {title}
    </div>
  </motion.div>
);

const AvailabilityMetricCard: React.FC<{
  title: string;
  count: number;
  color: string;
  icon: React.ReactNode;
}> = ({ title, count, color, icon }) => (
  <motion.div 
    variants={{
      hover: { 
        y: -2,
        transition: { duration: 0.2 }
      }
    }}
    whileHover="hover"
    className={`bg-gradient-to-r from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium text-gray-900">{title}</span>
      </div>
      <span className={`text-lg font-bold text-${color}-700`}>{count}</span>
    </div>
  </motion.div>
);

const AvailabilitySection: React.FC<{
  title: string;
  events: any[];
  color: string;
}> = ({ title, events, color }) => (
  <div className={`bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-xl p-4 border border-${color}-200`}>
    <h4 className={`font-semibold text-${color}-800 mb-3`}>{title} ({events.length})</h4>
    <div className="space-y-2 max-h-40 overflow-y-auto">
      {events.length > 0 ? (
        events.slice(0, 5).map((event, index) => (
          <div key={index} className={`bg-white/70 p-2 rounded-lg border border-${color}-200`}>
            <div className="text-sm font-medium text-gray-900">{event.time}</div>
            <div className="text-xs text-gray-600 truncate">{event.eventText}</div>
            <div className="text-xs text-gray-500">{event.date}</div>
          </div>
        ))
      ) : (
        <p className="text-gray-500 italic text-sm">No events</p>
      )}
      {events.length > 5 && (
        <p className="text-xs text-gray-500 italic">+{events.length - 5} more events</p>
      )}
    </div>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string | React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col md:flex-row md:justify-between gap-1 md:gap-4 py-2 border-b border-gray-100 last:border-0">
    <span className="text-gray-600 font-medium text-sm">{label}</span>
    <span className="font-semibold text-gray-900 text-sm md:text-right">{value}</span>
  </div>
);

const ProfileSection: React.FC<{ 
  title: string; 
  children: React.ReactNode;
  icon: React.ReactNode;
}> = ({ title, children, icon }) => (
  <div>
    <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-4 flex items-center pb-2 border-b border-gray-200">
      {icon}
      <span className="ml-2">{title}</span>
    </h2>
    {children}
  </div>
);

export default ClinicianAdminDetails;